/**
 * Hook dữ liệu của module `tham-dinh` (ADR-0080). View chỉ gọi hook ở đây, không gọi repository.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { luuBlob } from '../../../lib/api/http';
import { khoTamDinh } from '../../../lib/repositories/tham-dinh.repository';
import type {
  ThamSoBoTieuChi,
  ThamSoDanhSach,
  ThamSoLoaiHoSo,
  ThamSoPhien,
} from '../../../lib/repositories/tham-dinh.repository';
import { useToast } from '../../../lib/toast';
import { SO_DONG_TRANG } from '../constants';
import type {
  BanDanhGia,
  BoTieuChi,
  BoTieuChiChiTiet,
  BoTieuChiSua,
  BoTieuChiTao,
  KetQuaCanTrongSo,
  KetQuaTieuChi,
  GoiTroLy,
  LuotApSua,
  LuotGoiY,
  LuotHoiDap,
  TrichDanTroLy,
  SoSanhPhien,
  KhoaMoiTao,
  KhoaTichHop,
  KhoaTichHopTao,
  LinhVuc,
  LinhVucSua,
  LinhVucTao,
  LoaiHoSo,
  LoaiHoSoSua,
  LoaiHoSoTao,
  LuuNhap,
  MucHangCho,
  NutLinhVucCay,
  Phien,
  PhienChiTiet,
  PhienTao,
  SuaKetQua,
  TongQuan,
  Trang,
} from '../types';

/** Khoá cache: mọi thứ của module nằm dưới `['tham-dinh']`, vô hiệu hoá theo nhánh. */
export const KHOA = {
  goc: ['tham-dinh'] as const,
  linhVuc: ['tham-dinh', 'linh-vuc'] as const,
  linhVucLoc: (search: string) => ['tham-dinh', 'linh-vuc', 'ds', search] as const,
  loaiHoSo: ['tham-dinh', 'loai-ho-so'] as const,
  loaiHoSoLoc: (linhVuc: string | undefined, search: string) =>
    ['tham-dinh', 'loai-ho-so', 'ds', linhVuc ?? 'tat-ca', search] as const,
  bo: ['tham-dinh', 'bo-tieu-chi'] as const,
  boLoc: (loai: string | undefined, trangThai: string | undefined, search: string) =>
    ['tham-dinh', 'bo-tieu-chi', 'ds', loai ?? 'tat-ca', trangThai ?? 'tat-ca', search] as const,
  boChiTiet: (id: string) => ['tham-dinh', 'bo-tieu-chi', 'chi-tiet', id] as const,
  goiY: (boId: string, luotId: string) => ['tham-dinh', 'bo-tieu-chi', 'goi-y', boId, luotId] as const,
  soSanh: (phienId: string) => ['tham-dinh', 'phien', 'so-sanh', phienId] as const,
  danhGia: (phienId: string) => ['tham-dinh', 'phien', 'danh-gia', phienId] as const,
  apSua: (phienId: string) => ['tham-dinh', 'phien', 'ap-sua', phienId] as const,
  troLy: (phienId: string) => ['tham-dinh', 'phien', 'tro-ly', phienId] as const,
  cay: ['tham-dinh', 'cay'] as const,
  tongQuan: ['tham-dinh', 'tong-quan'] as const,
  phien: ['tham-dinh', 'phien'] as const,
  phienLoc: (trangThai: string | undefined, search: string) =>
    ['tham-dinh', 'phien', 'ds', trangThai ?? 'tat-ca', search] as const,
  phienChiTiet: (id: string) => ['tham-dinh', 'phien', 'chi-tiet', id] as const,
  ketQua: (id: string) => ['tham-dinh', 'phien', 'ket-qua', id] as const,
  hangCho: ['tham-dinh', 'phe-duyet', 'hang-cho'] as const,
  khoa: ['tham-dinh', 'tich-hop', 'khoa'] as const,
};

/**
 * 4xx là câu trả lời của server, không phải trục trặc: thử lại chỉ bắt người dùng chờ thêm.
 *
 * Quan trọng với module này vì bốn màn của đợt sau gọi route chưa mở và nhận 404 — thử lại ba lần
 * biến một màn "chưa có" thành ba lượt chờ rồi mới báo.
 */
function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

function useLamMoi() {
  const qc = useQueryClient();
  return (khoa: readonly unknown[]) => void qc.invalidateQueries({ queryKey: khoa });
}

/* ----- Lĩnh vực ------------------------------------------------------------------------------------ */

export function useLinhVuc(q: ThamSoDanhSach = {}) {
  return useQuery<Trang<LinhVuc>, ApiError>({
    queryKey: KHOA.linhVucLoc(q.search ?? ''),
    queryFn: () => khoTamDinh.danhSachLinhVuc({ limit: SO_DONG_TRANG, ...q }),
    retry: thuLai,
  });
}

export function useTaoLinhVuc() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, LinhVucTao>({
    mutationFn: (du) => khoTamDinh.taoLinhVuc(du),
    onSuccess: (_, du) => {
      lamMoi(KHOA.linhVuc);
      lamMoi(KHOA.cay);
      toast.success(t('thamDinh.linhVuc.daTao', { ten: du.ten }));
    },
  });
}

export function useSuaLinhVuc() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, { id: string; du: LinhVucSua }>({
    mutationFn: ({ id, du }) => khoTamDinh.suaLinhVuc(id, du),
    onSuccess: () => {
      lamMoi(KHOA.linhVuc);
      lamMoi(KHOA.cay);
      toast.success(t('thamDinh.linhVuc.daLuu'));
    },
  });
}

export function useXoaLinhVuc() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, { id: string; ten: string }>({
    mutationFn: ({ id }) => khoTamDinh.xoaLinhVuc(id),
    onSuccess: (_, { ten }) => {
      lamMoi(KHOA.linhVuc);
      lamMoi(KHOA.cay);
      toast.success(t('thamDinh.linhVuc.daXoa', { ten }));
    },
  });
}

/* ----- Loại hồ sơ ---------------------------------------------------------------------------------- */

export function useLoaiHoSo(q: ThamSoLoaiHoSo = {}) {
  return useQuery<Trang<LoaiHoSo>, ApiError>({
    queryKey: KHOA.loaiHoSoLoc(q.linh_vuc_id, q.search ?? ''),
    queryFn: () => khoTamDinh.danhSachLoaiHoSo({ limit: SO_DONG_TRANG, ...q }),
    retry: thuLai,
  });
}

export function useTaoLoaiHoSo() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, LoaiHoSoTao>({
    mutationFn: (du) => khoTamDinh.taoLoaiHoSo(du),
    onSuccess: (_, du) => {
      lamMoi(KHOA.loaiHoSo);
      lamMoi(KHOA.linhVuc);
      lamMoi(KHOA.cay);
      toast.success(t('thamDinh.loaiHoSo.daTao', { ten: du.ten }));
    },
  });
}

export function useSuaLoaiHoSo() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, { id: string; du: LoaiHoSoSua }>({
    mutationFn: ({ id, du }) => khoTamDinh.suaLoaiHoSo(id, du),
    onSuccess: () => {
      lamMoi(KHOA.loaiHoSo);
      lamMoi(KHOA.cay);
      toast.success(t('thamDinh.loaiHoSo.daLuu'));
    },
  });
}

export function useXoaLoaiHoSo() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, { id: string; ten: string }>({
    mutationFn: ({ id }) => khoTamDinh.xoaLoaiHoSo(id),
    onSuccess: (_, { ten }) => {
      lamMoi(KHOA.loaiHoSo);
      lamMoi(KHOA.linhVuc);
      lamMoi(KHOA.cay);
      toast.success(t('thamDinh.loaiHoSo.daXoa', { ten }));
    },
  });
}

/* ----- Bộ tiêu chí --------------------------------------------------------------------------------- */

export function useBoTieuChi(q: ThamSoBoTieuChi = {}) {
  return useQuery<Trang<BoTieuChi>, ApiError>({
    queryKey: KHOA.boLoc(q.loai_ho_so_id, q.trang_thai, q.search ?? ''),
    queryFn: () => khoTamDinh.danhSachBo({ limit: SO_DONG_TRANG, ...q }),
    retry: thuLai,
  });
}

export function useBoChiTiet(id: string | undefined) {
  return useQuery<BoTieuChiChiTiet, ApiError>({
    queryKey: KHOA.boChiTiet(id ?? ''),
    queryFn: () => khoTamDinh.bo(id!),
    enabled: Boolean(id),
    retry: thuLai,
  });
}

export function useCayThuVien() {
  return useQuery<NutLinhVucCay[], ApiError>({ queryKey: KHOA.cay, queryFn: () => khoTamDinh.cay(), retry: thuLai });
}

export function useTaoBo() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, BoTieuChiTao>({
    mutationFn: (du) => khoTamDinh.taoBo(du),
    onSuccess: (_, du) => {
      lamMoi(KHOA.bo);
      lamMoi(KHOA.cay);
      toast.success(t('thamDinh.bo.daTao', { ten: du.ten }));
    },
  });
}

export function useSuaBo() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, { id: string; du: BoTieuChiSua }>({
    mutationFn: ({ id, du }) => khoTamDinh.suaBo(id, du),
    onSuccess: (_, { id }) => {
      lamMoi(KHOA.bo);
      lamMoi(KHOA.boChiTiet(id));
      lamMoi(KHOA.cay);
      toast.success(t('thamDinh.bo.daLuu'));
    },
  });
}

export function useXoaBo() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, { id: string; ten: string }>({
    mutationFn: ({ id }) => khoTamDinh.xoaBo(id),
    onSuccess: (_, { ten }) => {
      lamMoi(KHOA.bo);
      lamMoi(KHOA.cay);
      toast.success(t('thamDinh.bo.daXoa', { ten }));
    },
  });
}

/**
 * Lưu nháp. `409` ở đây là câu trả lời có nghĩa — ai đó vừa lưu bản khác — nên view hiện nguyên câu của
 * backend (nó mang tên người vừa lưu) thay vì toast chung chung.
 */
export function useLuuNhap() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, { id: string; du: LuuNhap }>({
    mutationFn: ({ id, du }) => khoTamDinh.luuNhap(id, du),
    onSuccess: (_, { id }) => {
      lamMoi(KHOA.boChiTiet(id));
      lamMoi(KHOA.bo);
      toast.success(t('thamDinh.bo.daLuuNhap'));
    },
  });
}

export function usePhatHanh() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, { id: string; ghiChu: string }>({
    mutationFn: ({ id, ghiChu }) => khoTamDinh.phatHanh(id, ghiChu),
    onSuccess: (_, { id }) => {
      lamMoi(KHOA.bo);
      lamMoi(KHOA.boChiTiet(id));
      lamMoi(KHOA.cay);
      toast.success(t('thamDinh.bo.daPhatHanh'));
    },
  });
}

/* ----- Đợt sau: route chưa mở ---------------------------------------------------------------------- */

export function useTongQuan() {
  return useQuery<TongQuan, ApiError>({ queryKey: KHOA.tongQuan, queryFn: () => khoTamDinh.tongQuan(), retry: thuLai });
}

export function usePhien(q: ThamSoPhien = {}) {
  return useQuery<Trang<Phien>, ApiError>({
    queryKey: KHOA.phienLoc(q.trang_thai, q.search ?? ''),
    queryFn: () => khoTamDinh.danhSachPhien({ limit: SO_DONG_TRANG, ...q }),
    retry: thuLai,
  });
}

export function usePhienChiTiet(id: string | undefined) {
  return useQuery<PhienChiTiet, ApiError>({
    queryKey: KHOA.phienChiTiet(id ?? ''),
    queryFn: () => khoTamDinh.phien(id!),
    enabled: Boolean(id),
    retry: thuLai,
  });
}

/** Nộp hồ sơ. Trích văn bản chạy trong chính lời gọi này nên nó lâu hơn một POST thường. */
export function useTaoPhien() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<PhienChiTiet, ApiError, { du: PhienTao; tep: File[] }>({
    mutationFn: ({ du, tep }) => khoTamDinh.taoPhien(du, tep),
    onSuccess: (p) => {
      lamMoi(KHOA.phien);
      lamMoi(KHOA.tongQuan);
      toast.success(t('thamDinh.phien.daNop', { ten: p.tieu_de }));
    },
  });
}

export function useXoaPhien() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, { id: string; ten: string }>({
    mutationFn: ({ id }) => khoTamDinh.xoaPhien(id),
    onSuccess: (_, { ten }) => {
      lamMoi(KHOA.phien);
      lamMoi(KHOA.tongQuan);
      toast.success(t('thamDinh.phien.daXoa', { ten }));
    },
  });
}

export function useKetQuaPhien(id: string | undefined) {
  return useQuery<KetQuaTieuChi[], ApiError>({
    queryKey: KHOA.ketQua(id ?? ''),
    queryFn: () => khoTamDinh.ketQuaPhien(id!),
    enabled: Boolean(id),
    retry: thuLai,
  });
}

/**
 * Ghi đè phán quyết. Không toast mỗi lần: người thẩm định sửa liên tiếp nhiều tiêu chí, và một chuỗi toast
 * "Đã lưu" che mất chính bảng họ đang đọc. Lỗi vẫn hiện — đó mới là thứ cần dừng họ lại.
 */
export function useSuaKetQua() {
  const lamMoi = useLamMoi();
  return useMutation<PhienChiTiet, ApiError, { phienId: string; ma: string; du: SuaKetQua }>({
    mutationFn: ({ phienId, ma, du }) => khoTamDinh.suaKetQua(phienId, ma, du),
    onSuccess: (_, { phienId }) => {
      lamMoi(KHOA.ketQua(phienId));
      lamMoi(KHOA.phienChiTiet(phienId));
    },
  });
}

export function useHoanTatPhien() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<PhienChiTiet, ApiError, string>({
    mutationFn: (id) => khoTamDinh.hoanTatPhien(id),
    onSuccess: (p) => {
      lamMoi(KHOA.phien);
      lamMoi(KHOA.phienChiTiet(p.id));
      lamMoi(KHOA.ketQua(p.id));
      lamMoi(KHOA.hangCho);
      lamMoi(KHOA.tongQuan);
      toast.success(t(p.trang_thai === 'hoan_tat' ? 'thamDinh.raSoat.daHoanTat' : 'thamDinh.raSoat.daTrinh'));
    },
  });
}

/** Nhịp poll một lượt gợi ý đang chạy: tra cứu web mất 20–40 giây nên 2 giây là đủ mịn. */
const NHIP_GOI_Y = 2_000;

/** Mở một lượt AI gợi ý tiêu chí; trả về lượt để `useLuotGoiY` poll tiếp. */
export function useBatDauGoiY() {
  const { t } = useT();
  const toast = useToast();
  return useMutation<LuotGoiY, ApiError, { id: string; mo_ta: string; tep?: File }>({
    mutationFn: ({ id, mo_ta, tep }) => khoTamDinh.batDauGoiY(id, mo_ta, tep),
    onSuccess: () => toast.info(t('thamDinh.soan.goiYDangChay')),
  });
}

/**
 * Poll một lượt gợi ý tới khi xong hoặc lỗi.
 *
 * `luotId` rỗng thì không gọi gì: hook này sống suốt màn soạn, còn lượt chỉ có sau khi người soạn bấm.
 */
export function useLuotGoiY(boId: string | undefined, luotId: string | undefined) {
  return useQuery<LuotGoiY, ApiError>({
    queryKey: KHOA.goiY(boId ?? '', luotId ?? ''),
    queryFn: () => khoTamDinh.docGoiY(boId!, luotId!),
    enabled: !!boId && !!luotId,
    // Dừng poll khi đã xong hay đã lỗi — để chạy tiếp là gọi API mãi cho một kết quả không đổi
    refetchInterval: (q) => (q.state.data && ['cho', 'dang_chay'].includes(q.state.data.trang_thai)
      ? NHIP_GOI_Y : false),
  });
}

/**
 * AI cân trọng số. **Không** làm mới cache bộ tiêu chí: backend không ghi gì, nên bản nháp trên máy chủ chưa
 * đổi — làm mới ở đây là xoá mất đề xuất người soạn đang xem.
 */
export function useCanTrongSo() {
  const { t } = useT();
  const toast = useToast();
  return useMutation<KetQuaCanTrongSo, ApiError, string>({
    mutationFn: (id) => khoTamDinh.canTrongSo(id),
    onSuccess: (kq) => {
      if (kq.da_doi.length === 0) toast.info(t('thamDinh.soan.canKhongDoi'));
    },
  });
}

/**
 * Thử webhook của một khoá.
 *
 * Toast hiện **câu kết quả của backend**, không phải "đã gửi": một webhook gửi đi mà bên nhận trả 401 vẫn là
 * gửi xong, và người cấu hình cần thấy đúng con số ấy. Làm mới danh sách để cột "lần gửi cuối" đổi theo.
 */
export function useThuWebhook() {
  const toast = useToast();
  const lamMoi = useLamMoi();
  return useMutation<{ ket_qua: string }, ApiError, string>({
    mutationFn: (id) => khoTamDinh.thuWebhook(id),
    onSuccess: (kq) => {
      lamMoi(KHOA.khoa);
      // 2xx là nhận được; còn lại là một câu cần đọc kỹ
      if (/^2\d\d/.test(kq.ket_qua)) toast.success(kq.ket_qua);
      else toast.error(kq.ket_qua);
    },
  });
}

/** Lịch sử hỏi–đáp của chính người gọi. */
export function useLichSuTroLy(id: string | undefined) {
  return useQuery<Trang<LuotHoiDap>, ApiError>({
    queryKey: KHOA.troLy(id ?? ''),
    queryFn: () => khoTamDinh.lichSuTroLy(id!),
    enabled: !!id,
  });
}

/** Một lượt hỏi đang chạy trên màn hình. */
export interface LuotDangHoi {
  cauHoi: string;
  /** Câu trả lời dựng dần từ các gói `van`. */
  dap: string;
  chang: Array<{ step: string; detail: string; status: 'ok' | 'running' | 'warn' }>;
  trichDan: TrichDanTroLy[];
  loi: string;
  xong: boolean;
}

const LUOT_RONG: LuotDangHoi = { cauHoi: '', dap: '', chang: [], trichDan: [], loi: '', xong: false };

/**
 * Hỏi trợ lí và đọc luồng SSE.
 *
 * Không dùng `useMutation`: câu trả lời tới **dần**, nên state phải cập nhật nhiều lần trong một lượt —
 * `useMutation` chỉ có một `onSuccess`.
 *
 * **Chặng trùng tên thì THAY, khác tên thì THÊM** (đặc tả mục 9.6): một dòng cảnh báo mang tên riêng nên nó
 * không ghi đè chặng vừa `ok`.
 */
export function useHoiTroLy(phienId: string | undefined) {
  const { t } = useT();
  const lamMoi = useLamMoi();
  const [luot, setLuot] = useState<LuotDangHoi>(LUOT_RONG);
  const [dangHoi, setDangHoi] = useState(false);
  const dungRef = useRef<(() => void) | undefined>(undefined);

  const dung = useCallback(() => {
    dungRef.current?.();
    dungRef.current = undefined;
    setDangHoi(false);
  }, []);

  // Rời màn giữa lượt: huỷ luồng để server dừng lượt gọi mô hình
  useEffect(() => dung, [dung]);

  const hoi = useCallback(async (cauHoi: string) => {
    if (!phienId || dangHoi) return;
    setLuot({ ...LUOT_RONG, cauHoi });
    setDangHoi(true);
    const { khung, dung: huy } = khoTamDinh.hoiTroLy(phienId, cauHoi);
    dungRef.current = huy;
    try {
      for await (const k of khung) {
        let goi: GoiTroLy;
        try {
          goi = JSON.parse(k.data) as GoiTroLy;
        } catch {
          continue; // khung không phải JSON: bỏ qua, không làm sập lượt
        }
        setLuot((cu) => apGoi(cu, goi));
      }
    } catch (e) {
      /*
       * Lỗi **trước** khi luồng mở (401, 403, 503, mạng đứt): câu của `moLuongPost` đã là câu cho người dùng
       * khi backend trả problem+json. Lỗi **trong** luồng đi qua gói `error` nên không tới đây.
       */
      const cau = e instanceof Error && e.message && !e.message.startsWith('luong-khong-mo-duoc')
        ? e.message
        : t('thamDinh.raSoat.troLyKhongMoDuoc');
      setLuot((cu) => ({ ...cu, loi: cau, xong: true }));
    } finally {
      dungRef.current = undefined;
      setDangHoi(false);
      lamMoi(KHOA.troLy(phienId));
    }
  }, [phienId, dangHoi, lamMoi, t]);

  return { luot, dangHoi, hoi, dung };
}

/** Áp một gói SSE vào trạng thái lượt hỏi. */
function apGoi(cu: LuotDangHoi, goi: GoiTroLy): LuotDangHoi {
  switch (goi.type) {
    case 'van':
      return { ...cu, dap: cu.dap + goi.delta };
    case 'step': {
      // Trùng tên thì THAY, khác tên thì THÊM (đặc tả mục 9.6)
      const i = cu.chang.findIndex((c) => c.step === goi.step);
      const moi = { step: goi.step, detail: goi.detail, status: goi.status };
      return { ...cu, chang: i >= 0
        ? cu.chang.map((c, j) => (j === i ? moi : c))
        : [...cu.chang, moi] };
    }
    case 'error':
      return { ...cu, loi: goi.message, xong: true };
    case 'done':
      // `answer` là TOÀN VĂN đã chốt, không phải mẩu cuối: lấy nó thay cho phần dựng dần
      return { ...cu, dap: goi.result.answer, trichDan: goi.result.trichDan, xong: true };
  }
}

/** Nhịp poll lượt áp sửa: một lượt gửi cả hồ sơ cho mô hình rồi dựng hồ sơ mới. */
const NHIP_AP_SUA = 3_000;

/** Tick đồng ý / từ chối một gợi ý sửa. */
export function useQuyetGoiYSua() {
  const lamMoi = useLamMoi();
  return useMutation<{ ma: string; sua_trang_thai: string }, ApiError,
    { phienId: string; ma: string; dongY: boolean | null }>({
    mutationFn: ({ phienId, ma, dongY }) => khoTamDinh.quyetGoiYSua(phienId, ma, dongY),
    onSuccess: (_d, v) => lamMoi(KHOA.ketQua(v.phienId)),
  });
}

/** Lượt áp gợi ý sửa, poll khi đang chạy. */
export function useLuotApSua(id: string | undefined) {
  return useQuery<LuotApSua, ApiError>({
    queryKey: KHOA.apSua(id ?? ''),
    queryFn: () => khoTamDinh.docApSua(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && ['cho', 'dang_chay'].includes(q.state.data.trang_thai)
      ? NHIP_AP_SUA : false),
  });
}

/** Bấm "Áp dụng sửa". */
export function useApDungSua() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<LuotApSua, ApiError, { phienId: string; ma?: string[] }>({
    mutationFn: ({ phienId, ma }) => khoTamDinh.apDungSua(phienId, ma ?? []),
    onSuccess: (_d, v) => {
      lamMoi(KHOA.apSua(v.phienId));
      toast.info(t('thamDinh.raSoat.apSuaDangChay'));
    },
  });
}

/** Nhịp poll bản đánh giá đang viết: một lời gọi mô hình mất 30–90 giây. */
const NHIP_DANH_GIA = 3_000;

/**
 * Bản đánh giá của một hồ sơ, poll khi đang viết.
 *
 * Dừng poll ở `xong`, `loi` và **`chua_co`**: `chua_co` nghĩa là chưa ai chạy, nên gọi lại mãi là gọi API
 * cho một câu trả lời không đổi tới khi người dùng bấm.
 */
export function useDanhGiaPhien(id: string | undefined) {
  return useQuery<BanDanhGia, ApiError>({
    queryKey: KHOA.danhGia(id ?? ''),
    queryFn: () => khoTamDinh.danhGiaPhien(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && ['cho', 'dang_chay'].includes(q.state.data.trang_thai)
      ? NHIP_DANH_GIA : false),
  });
}

/** Viết lại bản đánh giá. */
export function useVietLaiDanhGia() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<BanDanhGia, ApiError, string>({
    mutationFn: (id) => khoTamDinh.vietLaiDanhGia(id),
    onSuccess: (_d, id) => {
      lamMoi(KHOA.danhGia(id));
      toast.info(t('thamDinh.raSoat.danhGiaDangViet'));
    },
  });
}

/** Nộp lại hồ sơ: điều hướng sang phiên mới là việc của view, hook chỉ trả phiên. */
export function useNopLaiPhien() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<PhienChiTiet, ApiError, { id: string; tieuDe: string; tep: File[] }>({
    mutationFn: ({ id, tieuDe, tep }) => khoTamDinh.nopLaiPhien(id, tieuDe, tep),
    onSuccess: (p) => {
      lamMoi(KHOA.phien);
      lamMoi(KHOA.phienChiTiet(p.id));
      lamMoi(KHOA.tongQuan);
      toast.success(t('thamDinh.raSoat.daNopLai'));
    },
  });
}

/** So sánh với lần nộp trước. `enabled` tắt khi hồ sơ là lần nộp đầu — backend trả 409 cho trường hợp đó. */
export function useSoSanhPhien(id: string | undefined, coLanTruoc: boolean) {
  return useQuery<SoSanhPhien, ApiError>({
    queryKey: KHOA.soSanh(id ?? ''),
    queryFn: () => khoTamDinh.soSanhPhien(id!),
    enabled: !!id && coLanTruoc,
  });
}

/** Chạy thử bộ tiêu chí theo bản nháp. */
export function useChayThuBo() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<PhienChiTiet, ApiError, { boId: string; tieuDe: string; tep: File[] }>({
    mutationFn: ({ boId, tieuDe, tep }) => khoTamDinh.chayThuBo(boId, tieuDe, tep),
    onSuccess: (p) => {
      lamMoi(KHOA.phienChiTiet(p.id));
      toast.info(t('thamDinh.soan.chayThuDangCham'));
    },
  });
}

/** Chấm lại hồ sơ lỗi hoặc hồ sơ đang rà soát; trả về phiên đã về `cho_xu_ly` để thanh tiến trình hiện lại. */
export function useChamLaiPhien() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<PhienChiTiet, ApiError, string>({
    mutationFn: (id) => khoTamDinh.chamLaiPhien(id),
    onSuccess: (p) => {
      lamMoi(KHOA.phien);
      lamMoi(KHOA.phienChiTiet(p.id));
      lamMoi(KHOA.ketQua(p.id));
      lamMoi(KHOA.tongQuan);
      toast.success(t('thamDinh.raSoat.daDayChamLai'));
    },
  });
}

/**
 * Tải biên bản .docx về máy.
 *
 * Dùng mutation chứ không query: đây là một hành động người bấm, không phải dữ liệu cần đệm — và đệm một
 * Blob vài trăm KB theo id phiên là giữ lại trong RAM thứ người dùng đã lưu xong.
 */
export function useBienBanPhien() {
  const toast = useToast();
  const { t } = useT();
  return useMutation<void, ApiError, { id: string; tieuDe: string }>({
    mutationFn: async ({ id, tieuDe }) => {
      const { blob, ten } = await khoTamDinh.bienBanPhien(id, `${tieuDe}.docx`);
      luuBlob(blob, ten);
    },
    onSuccess: () => toast.success(t('thamDinh.raSoat.daTaiBienBan')),
  });
}

export function usePheDuyetPhien() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, { id: string; yKien: string; traLai: boolean }>({
    mutationFn: ({ id, yKien, traLai }) =>
      traLai ? khoTamDinh.traLaiPhien(id, yKien) : khoTamDinh.pheDuyetPhien(id, yKien),
    onSuccess: (_, { id, traLai }) => {
      lamMoi(KHOA.phien);
      lamMoi(KHOA.phienChiTiet(id));
      lamMoi(KHOA.hangCho);
      lamMoi(KHOA.tongQuan);
      toast.success(t(traLai ? 'thamDinh.raSoat.daTraLai' : 'thamDinh.raSoat.daPheDuyet'));
    },
  });
}

export function useHangChoPheDuyet() {
  return useQuery<Trang<MucHangCho>, ApiError>({ queryKey: KHOA.hangCho, queryFn: () => khoTamDinh.hangChoPheDuyet(), retry: thuLai });
}

export function useKhoaTichHop() {
  return useQuery<Trang<KhoaTichHop>, ApiError>({ queryKey: KHOA.khoa, queryFn: () => khoTamDinh.danhSachKhoa(), retry: thuLai });
}

/**
 * Tạo khoá API. **Không** toast ở đây: phản hồi mang khoá nguyên văn mà người dùng phải chép ngay, nên view
 * giữ hộp mở để hiện nó — một toast "đã tạo" rồi đóng hộp là làm mất khoá vĩnh viễn.
 */
export function useTaoKhoa() {
  const lamMoi = useLamMoi();
  return useMutation<KhoaMoiTao, ApiError, KhoaTichHopTao>({
    mutationFn: (du) => khoTamDinh.taoKhoa(du),
    onSuccess: () => lamMoi(KHOA.khoa),
  });
}

export function useThuHoiKhoa() {
  const toast = useToast();
  const { t } = useT();
  const lamMoi = useLamMoi();
  return useMutation<unknown, ApiError, { id: string; ten: string }>({
    mutationFn: ({ id }) => khoTamDinh.thuHoiKhoa(id),
    onSuccess: (_, { ten }) => {
      lamMoi(KHOA.khoa);
      toast.success(t('thamDinh.khoa.daThuHoi', { ten }));
    },
  });
}
